import React from "react";
import {
useTable,
useResizeColumns,
useFlexLayout, useGlobalFilter, useFilters, useAsyncDebounce,
useRowSelect,
useSortBy
} from "react-table";
import { matchSorter } from 'match-sorter';
import { Button, Input, Row, Col, Container } from 'reactstrap';
import DatePickerControl from "../Form/DatePicker";
import Editor from "./Editor";
import entities from "../../utils/entities";
import { useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileExcel } from '@fortawesome/free-solid-svg-icons'
import { useAuth } from "../../Services/AuthContext";

/*
// This is a custom filter UI for selecting
// a unique option from a list
function SelectColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
}) {
    // Calculate the options for filtering
    // using the preFilteredRows
    const options = React.useMemo(() => {
        const options = new Set()
        preFilteredRows.forEach(row => {
            options.add(row.values[id])
        })
        return [...options.values()]
    }, [id, preFilteredRows])

    // Render a multi-select box
    return (
        <select
            value={filterValue}
            onChange={
                e => {
                    setFilter(e.target.value || undefined)
                }}
        >
            <option value=""> All </option>
            {
                options.map((option, i) => (

                    <option key={i}
                        value={option}>
                        { option}
                    </option>
                ))}
        </select>
    )
}
const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (

        <>

            <Input type = "checkbox" ref= { resolvedRef } { ...rest}/>
   
               </>
        );
}
);

// This is a custom filter UI that uses a
// slider to set the filter value between a column's
// min and max values
function SliderColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
}) {
    // Calculate the min and max
    // using the preFilteredRows

    const [min, max] = React.useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        preFilteredRows.forEach(row => {
            min = Math.min(row.values[id], min)
            max = Math.max(row.values[id], max)
        })
        return [min, max]
    }, [id, preFilteredRows])

    return (
        <>
            <Input
                type = "range"
                min ={ min}
    max ={ max}
    value ={ filterValue || min}
    onChange ={
        e => {
            setFilter(parseInt(e.target.value, 10))
                }}
    />
            <button onClick ={ () => setFilter(undefined)}> Off </button>
   
           </>
    )
}

// This is a custom UI for our 'between' or number range
// filter. It uses two number boxes and filters rows to
// ones that have values between the two
function NumberRangeColumnFilter({
    column: { filterValue = [], preFilteredRows, setFilter, id },
}) {
    const [min, max] = React.useMemo(() => {
        let min = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        let max = preFilteredRows.length ? preFilteredRows[0].values[id] : 0
        preFilteredRows.forEach(row => {
            min = Math.min(row.values[id], min)
            max = Math.max(row.values[id], max)
        })
        return [min, max]
    }, [id, preFilteredRows])

    return (
        <React.Fragment style ={{display: 'flex',}}>
            <Input value ={ filterValue[0] || ''}type = "number"onChange ={
        e => {
            const val = e.target.value
                    setFilter((old = []) => [val ? parseInt(val, 10) : undefined, old[1]])
                }} placeholder ={`Min(${ min})`} />
      to
            <Input
                value ={ filterValue[1] || ''}
    type = "number"
                onChange ={
        e => {
            const val = e.target.value
                    setFilter((old = []) => [old[0], val ? parseInt(val, 10) : undefined])
                }}
    placeholder ={`Max(${ max})`}


    />
        </React.Fragment>
    )
}
*/

// Define a default UI for filtering
function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter },
}) {
    const count = preFilteredRows.length

    return (
        <input style={{ marginLeft: '5px', marginRight: '5px', overflow: 'hidden', display: 'block' }}
            value={filterValue || ''}
            onChange={
                e => {
                    setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
                }}
            placeholder={`Search ${count} records...`}
        />
    )
}
function fuzzyTextFilterFn(rows, id, filterValue) {
    return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}


// Define a default UI for filtering
function GlobalFilter({
    preGlobalFilteredRows,
    globalFilter,
    setGlobalFilter,
}) {
    const count = preGlobalFilteredRows.length
    const [value, setValue] = React.useState(globalFilter)
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value || undefined)
    }, 200)

    return (
        <span>
            Search:{' '}
            <Input
                value={value || ""}
                onChange={
                    e => {
                        setValue(e.target.value);
                        onChange(e.target.value);
                    }}
                placeholder={`${count} records...`}


            />
        </span>
    )
}

function filterGreaterThan(rows, id, filterValue) {
    return rows.filter(row => {
        const rowValue = row.values[id]
        return rowValue >= filterValue
    })
}

// This is an autoRemove method on the filter function that
// when given the new filter value and returns true, the filter
// will be automatically removed. Normally this is just an undefined
// check, but here, we want to remove the filter if it's not a number
filterGreaterThan.autoRemove = val => typeof val !== 'number'



// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = val => !val

function GenericTable({ route, noData, entity, addEntity, filter, status, onStatusSelect, columns, data, actions, parentHeight, showBg, dateFrom, dateTo, fromChange, toChange }) {



    const defaultColumn = {
        // When using the useFlexLayout:
        minWidth: 30, // minWidth is only used as a limit for resizing
        width: 150, // width is used for both the flex-basis and flex-grow
        maxWidth: 200, // maxWidth is only used as a limit for resizing
        // Let's set up our default Filter UI
        Filter: DefaultColumnFilter,
    };
    const {
        getTableProps, headerGroups, rows, prepareRow, preGlobalFilteredRows,
        setGlobalFilter } = useTable(
            {
                columns,
                data,
                defaultColumn,

            },
            useResizeColumns,
            useFlexLayout,
            useFilters, // useFilters!
            useGlobalFilter, // useGlobalFilter!
            useSortBy,
            useRowSelect,
            (hooks) => {
                if (actions && actions.length) {
                    hooks.allColumns.push((columns) => [
                        // Let's make a column for selection
                        {
                            id: "selection",
                            disableResizing: true,
                            minWidth: 35,
                            width: 100,
                            maxWidth: 100,
                            // The header can use the table's getToggleAllRowsSelectedProps method
                            // to render a checkbox

                            // The cell can use the individual row's getToggleRowSelectedProps method
                            // to the render a checkbox
                            Cell: ({ row }) => (
                                <React.Fragment>
                                    {
                                        actions?.map((action, index) => {
                                            return <Button key={index} color="primary"
                                                onClick={() => action.action(row.original)}>{action.name}</Button>


                                        })}
                                </React.Fragment>
                            )
                        },
                        ...columns
                    ]);

                    hooks.useInstanceBeforeDimensions.push(({ headerGroups }) => {
                        // fix the parent group of the selection button to not be resizable
                        const selectionGroupHeader = headerGroups[0].headers[0];
                        selectionGroupHeader.canResize = false;
                    });
                }
            }
        );
    const generateSortingIndicator = column => {
        return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""
    }
    const noCaseSort = (rowA, rowB, columnId) => {

        const a = rowA.original[columnId]?.toLowerCase()

        const b = rowB.original[columnId]?.toLowerCase()

        if (a > b)
            return 1

        if (b > a)
            return -1

        return 0
    };
    const [excel, setExcel] = useState();
    const downloadExcel = () => {
        setExcel(true);
    };
    const [auth] = useAuth();


    return (<div >
        {excel ? <Editor asNewItem entity={entities.downloadExcel} route={route} data={data} editorClosed={() => setExcel()} /> : null}
        <div className="table-responsive" style={{ height: isNaN(parentHeight) ? undefined : parentHeight, minHeight: '260px', overflowY: 'scroll', overflowX: 'hidden' }}>
            <GlobalFilter preGlobalFilteredRows={preGlobalFilteredRows}
                setGlobalFilter={setGlobalFilter} />
            {entity && entity.showDates && <Row><Col>From <DatePickerControl value={dateFrom} onChange={fromChange} /> </Col><Col>To<DatePickerControl value={dateTo} onChange={toChange} /></Col></Row>}
            <table {...getTableProps()}
                className="table table-striped table-hover" style={{ tableLayout: 'fixed' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    {
                        headerGroups.map((headerGroup, i) => {
                            return <tr key={i} style={{ top: i === 0 ? 0 : null }}>
                                {
                                    headerGroup.headers.map((column, i) => {
                                        if (column.sortNoCase) column.sortType = noCaseSort;
                                        return <th key={i}
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10, width: column.width, minWidth: column.width }}>
                                            <Container style={{ zIndex: 0 }}>

                                                <Row>
                                                    {column.render("Header")}
                                                    {generateSortingIndicator(column)}

                                                    <Row>{column.canFilter ? column.render('Filter') : null}</Row>
                                                </Row>


                                                {/* Use column.getResizerProps to hook up the events correctly */}
                                                {
                                                    column.canResize && (<div {...column.getResizerProps()} className={`resizer ${column.isResizing ? "isResizing" : ""}`} />)}
                                            </Container>
                                            {addEntity && entity?.addSingle && !entity.hideNew && i === 0 ? <Button onClick={() => addEntity()}>New</Button> : null}
                                            {auth.role === 'Admin' && entity?.excelDownload && i === 0 ? <Button onClick={() => downloadExcel()}><FontAwesomeIcon icon={faFileExcel} /></Button> : null}

                                        </th>
                                    })}
                            </tr>
                        }
                        )
                    }

                </thead>
                <tbody>
                    {rows.filter(filter || (() => true)).map((row, i) => {
                        prepareRow(row);
                        const color = showBg && showBg(row.original) ? showBg(row.original) : 'transparent';
                        return (<tr key={i}
                            style={{ width: '100px', backgroundColor: color }}>
                            {
                                row.cells.map((cell, i) => {
                                    return (<td key={i}
                                        style={{ width: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}> {cell.render("Cell", { index: i })}
                                    </td>);
                                })}
                        </tr>
                        );
                    })}

                </tbody>
            </table>

            {rows && rows.length ? <h4>End of results</h4> : <h4>{noData ? noData : 'No records found'}</h4>}
        </div>
    </div>
    );
}



export default GenericTable;

